<template>
    <div class="vue-rental-option-modal-wrapper">
        <perfect-scrollbar>
            <div class="modal-header d-flex justify-content-between align-items-start">
                <h3 class="modal-title mb-4">Wachtwoord aanpassen</h3>

                <button class="close-button mt-1" @click="$modal.close()">
                    {{ translations.closeText }}
                </button>
            </div>

            <div class="modal-body">
                <div class="modal-description">
                    <div v-if="successMessage" class="alert alert-success">
                        {{ successMessage }}
                    </div>
                    <div v-if="errorMessage" class="alert alert-danger">
                        {{ errorMessage }}
                    </div>

                    <form @submit.prevent="handleSubmit" v-if="!successMessage">
                        <div class="form-group">
                            <label for="old_password">Huidig wachtwoord</label>
                            <input type="password" id="old_password" v-model="form.old_password" class="form-control" required>
                        </div>

                        <div class="form-group">
                            <div class="d-flex justify-content-between align-items-center">
                                <label for="new_password">Nieuw wachtwoord</label>
                                <button type="button" class="btn-link" @click="generatePassword">Genereer veilig wachtwoord</button>
                            </div>
                            <input type="password" id="new_password" v-model="form.new_password" class="form-control" required @input="checkStrength">
                            
                            <div class="password-strength mt-2" v-if="form.new_password">
                                <div class="strength-bar">
                                    <div class="strength-fill" :style="{ width: strengthPercent + '%', backgroundColor: strengthColor }"></div>
                                </div>
                                <small class="strength-text" :style="{ color: strengthColor }">{{ strengthLabel }}</small>
                                <ul class="strength-requirements">
                                    <li :class="{ 'met': form.new_password.length >= 12 }">Minimaal 12 tekens</li>
                                    <li :class="{ 'met': hasLetter && hasNumber }">Bevat letters en cijfers</li>
                                </ul>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="confirm_password">Bevestig nieuw wachtwoord</label>
                            <input type="password" id="confirm_password" v-model="form.confirm_password" class="form-control" required>
                            <small v-if="form.confirm_password && !passwordsMatch" class="text-danger">Wachtwoorden komen niet overeen</small>
                        </div>

                        <div class="modal-actions">
                            <button type="button" class="btn btn-outline" @click="$modal.close()">Annuleren</button>
                            <button type="submit" class="btn btn-primary" :disabled="loading || !isValid">
                                {{ loading ? 'Bezig met opslaan...' : 'Opslaan' }}
                            </button>
                        </div>
                    </form>
                    
                    <div v-else class="modal-actions">
                        <button type="button" class="btn btn-primary" @click="$modal.close()">Sluiten</button>
                    </div>
                </div>
            </div>
        </perfect-scrollbar>
    </div>
</template>

<script>
import translations from "../../mixins/translations";

export default {
    name: 'ChangePasswordModal',

    mixins: [translations],

    data() {
        return {
            form: {
                old_password: '',
                new_password: '',
                confirm_password: '',
            },
            loading: false,
            successMessage: '',
            errorMessage: '',
            strengthPercent: 0,
            strengthLabel: '',
            strengthColor: '#ddd',
            hasLetter: false,
            hasNumber: false,
        };
    },

    computed: {
        passwordsMatch() {
            return this.form.new_password === this.form.confirm_password;
        },

        isValid() {
            return (
                this.form.old_password &&
                this.form.new_password.length >= 12 &&
                this.hasLetter &&
                this.hasNumber &&
                this.passwordsMatch
            );
        }
    },

    methods: {
        checkStrength() {
            const pwd = this.form.new_password;
            this.hasLetter = /[a-zA-Z]/.test(pwd);
            this.hasNumber = /[0-9]/.test(pwd);
            
            let score = 0;
            if (pwd.length >= 12) score += 40;
            if (this.hasLetter) score += 20;
            if (this.hasNumber) score += 20;
            if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score += 20;

            this.strengthPercent = score;

            if (score < 40) {
                this.strengthLabel = 'Zwak';
                this.strengthColor = '#dc3545';
            } else if (score < 80) {
                this.strengthLabel = 'Matig';
                this.strengthColor = '#ffc107';
            } else {
                this.strengthLabel = 'Sterk';
                this.strengthColor = '#28a745';
            }
        },

        generatePassword() {
            const length = 16;
            const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
            let retVal = "";
            for (let i = 0, n = charset.length; i < length; ++i) {
                retVal += charset.charAt(Math.floor(Math.random() * n));
            }
            // Ensure at least one letter and one number
            if (!/[a-zA-Z]/.test(retVal) || !/[0-9]/.test(retVal)) {
                return this.generatePassword();
            }
            this.form.new_password = retVal;
            this.form.confirm_password = retVal;
            this.checkStrength();
        },

        handleSubmit() {
            if (!this.isValid) return;

            this.loading = true;
            this.errorMessage = '';
            this.successMessage = '';

            window.axios.post('/api/customer/change-password', {
                old_password: this.form.old_password,
                new_password: this.form.new_password,
                confirm_password: this.form.confirm_password
            })
            .then(response => {
                this.successMessage = response.data.message || 'Wachtwoord succesvol aangepast.';
                this.loading = false;
                this.$emit('password-changed');
            })
            .catch(error => {
                this.errorMessage = error.response?.data?.error || 'Er is een fout opgetreden.';
                this.loading = false;
            });
        }
    }
};
</script>

<style scoped>

.v-modal__content {
    max-width: 800px !important;
}

@media (min-width: 1200px) {
    .v-modal__content {
        max-width: 800px !important;
    }
}

.modal-title {
    margin: 0;
    font-size: 20px;
    color: #033239;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 0;
    padding-top: 1rem;
    font-weight: 600;
    color: #033239;
}

.form-control {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
}
.v-modal__content .modal-body .modal-description * {
    margin-bottom: 0px;
}

.btn-link {
    background: none;
    border: none;
    color: #00abc8;
    cursor: pointer;
    font-size: 13px;
    padding: 0;
    text-decoration: underline;
}

.password-strength {
    margin-top: 10px;
}

.strength-bar {
    height: 6px;
    background: #eee;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 4px;
}

.strength-fill {
    height: 100%;
    transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-text {
    font-weight: 600;
    font-size: 12px;
}

.strength-requirements {
    list-style: none;
    padding: 0;
    margin: 8px 0 0 0;
    font-size: 12px;
}

.strength-requirements li {
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
}

.strength-requirements li::before {
    content: '○';
}

.strength-requirements li.met {
    color: #28a745;
}

.strength-requirements li.met::before {
    content: '●';
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
}

.btn {
    padding: 12px 24px;
    border-radius: 4px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    line-height: 1.5rem;
    font-size: 1rem;
    margin-top: 0;
}

.btn-primary {
    background: #82db39;
    border: 1px solid #82db39;
    color: #033239;
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-outline {
    background: transparent;
    border: 1px solid #ccc;
    color: #666;
}

.alert {
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.alert-success {
    background: #d4edda;
    color: #155724;
}

.alert-danger {
    background: #f8d7da;
    color: #721c24;
}
</style>
